// Les fonctions du bureau (D-126) : le titre accordé tout seul, « Nathalie D. », ce qu'une fonction reçoit, pourquoi
// écrire à quelqu'un. La table des titres est la même que celle de la base (_function_title) : les cas de
// scripts/bench/titres-cases.json passent des deux côtés.

export type Topic = 'licences' | 'dons' | 'recus' | 'salles' | 'partenaires' | 'essais' | 'site';
export type Preset = 'presidence' | 'vice_presidence' | 'tresorerie' | 'secretariat';
export type Gender = 'F' | 'M';

/** Ce qu'une fonction peut recevoir (« Reçoit »), dans l'ordre de la fiche. */
export const TOPICS: { key: Topic; label: string }[] = [
  { key: 'licences', label: 'Licences et paiements' },
  { key: 'dons', label: 'Dons' },
  { key: 'recus', label: 'Reçus à valider' },
  { key: 'salles', label: 'Demandes de salle' },
  { key: 'partenaires', label: 'Partenaires' },
  { key: 'essais', label: 'Essais et demandes' },
  // D-140 : la fonction « Site et communication » modifie le site du club, écrit les actualités et publie seule.
  { key: 'site', label: 'Site et actualités' },
];
export const TOPIC_LABEL = Object.fromEntries(TOPICS.map(t => [t.key, t.label])) as Record<Topic, string>;

/** Les fonctions proposées (D-126), avec leurs réglages par défaut. */
export const PRESETS: { preset: Preset; name: string; can_sign: boolean; receives: Topic[] }[] = [
  { preset: 'presidence', name: 'Présidence', can_sign: true, receives: [] },
  { preset: 'vice_presidence', name: 'Vice-présidence', can_sign: false, receives: [] },
  { preset: 'tresorerie', name: 'Trésorerie', can_sign: true, receives: ['licences', 'dons', 'recus'] },
  { preset: 'secretariat', name: 'Secrétariat', can_sign: false, receives: ['essais'] },
];

const T: [string, string, string][] = [
  ['présidence', 'Présidente', 'Président'], ['président', 'Présidente', 'Président'], ['présidente', 'Présidente', 'Président'],
  ['co-présidence', 'Co-présidente', 'Co-président'], ['coprésidence', 'Co-présidente', 'Co-président'],
  ['co-président', 'Co-présidente', 'Co-président'], ['co-présidente', 'Co-présidente', 'Co-président'],
  ['présidence d\'honneur', 'Présidente d\'honneur', 'Président d\'honneur'], ['présidence d’honneur', 'Présidente d’honneur', 'Président d’honneur'],
  ['vice-présidence', 'Vice-présidente', 'Vice-président'], ['vice-président', 'Vice-présidente', 'Vice-président'],
  ['vice-présidente', 'Vice-présidente', 'Vice-président'],
  ['trésorerie', 'Trésorière', 'Trésorier'], ['trésorier', 'Trésorière', 'Trésorier'], ['trésorière', 'Trésorière', 'Trésorier'],
  ['trésorerie adjointe', 'Trésorière adjointe', 'Trésorier adjoint'], ['trésorier adjoint', 'Trésorière adjointe', 'Trésorier adjoint'],
  ['trésorière adjointe', 'Trésorière adjointe', 'Trésorier adjoint'], ['vice-trésorerie', 'Vice-trésorière', 'Vice-trésorier'],
  ['secrétariat', 'Secrétaire', 'Secrétaire'], ['secrétaire', 'Secrétaire', 'Secrétaire'],
  ['secrétariat adjoint', 'Secrétaire adjointe', 'Secrétaire adjoint'], ['secrétaire adjoint', 'Secrétaire adjointe', 'Secrétaire adjoint'],
  ['secrétaire adjointe', 'Secrétaire adjointe', 'Secrétaire adjoint'],
  ['correspondant', 'Correspondante', 'Correspondant'], ['correspondante', 'Correspondante', 'Correspondant'],
  ['correspondance', 'Correspondante', 'Correspondant'],
  ['délégué', 'Déléguée', 'Délégué'], ['déléguée', 'Déléguée', 'Délégué'],
];
const TITLES = new Map(T.map(([k, f, m]) => [k, { F: f, M: m }]));
const clean = (s: string) => s.trim().replace(/\s+/g, ' ');

/** Le titre accordé : « Présidente » pour une femme, « Président » pour un homme ; un nom libre ou un genre inconnu :
 *  le nom tel que saisi (« Responsable des salles », « Présidence »). */
export function functionTitle(name: string, gender: Gender | null | undefined): string {
  const t = gender ? TITLES.get(clean(name).toLowerCase())?.[gender] : undefined;
  return t ?? clean(name);
}

/** Le titre accordé connaît-il les deux genres pour ce nom ? (sinon la fiche ne propose pas d'accord) */
export const titleAgrees = (name: string) => TITLES.has(clean(name).toLowerCase())
  && TITLES.get(clean(name).toLowerCase())!.F !== TITLES.get(clean(name).toLowerCase())!.M;

/** « présidente » : le titre dans une phrase. */
export const lowerTitle = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);

/** « Nathalie D. » : le prénom et l'initiale du nom. */
export function shortPerson(full: string | null | undefined): string {
  const w = clean(full ?? '').split(' ').filter(Boolean);
  if (w.length < 2) return w[0] ?? '';
  return `${w.slice(0, -1).join(' ')} ${w[w.length - 1]!.charAt(0).toUpperCase()}.`;
}
/** « Nathalie » */
export const firstOf = (full: string | null | undefined) => clean(full ?? '').split(' ')[0] ?? '';

/** Pourquoi lui écrire (« Qui contacter », maquette f3) : d'après la fonction et ce qu'elle reçoit. */
export function contactHint(preset: Preset | null, receives: Topic[]): string {
  if (preset === 'presidence' || (preset === 'vice_presidence' && !receives.length)) return 'Le club, une question générale';
  const parts: string[] = [];
  if (receives.includes('licences')) parts.push('licence, cotisation, paiement');
  if (receives.includes('essais')) parts.push('inscription, essai, attestation');
  if (receives.includes('salles')) parts.push('le club-house, une salle');
  if (receives.includes('partenaires')) parts.push('partenariat, mécénat');
  if (receives.includes('site')) parts.push('le site du club, une actualité');
  if (receives.includes('dons') || receives.includes('recus')) parts.push('reçu fiscal');
  if (!parts.length) return 'Une question pour cette fonction';
  const s = parts.join(', ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}
